import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';

const PaymentDebug: React.FC = () => {
  const { user, businessId } = useAuth();
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkDatabaseSetup = async () => {
      try {
        setLoading(true);
        const info: any = {
          user: user ? { id: user.id, email: user.email } : null,
          businessId,
          timestamp: new Date().toISOString()
        };

        // Check if accounts table exists and has required columns
        try {
          const { data: accounts, error: accountsError } = await supabase
            .from('accounts')
            .select('*')
            .limit(1);

          info.accountsTable = {
            exists: !accountsError,
            error: accountsError?.message,
            sampleData: accounts?.[0] || null
          };
        } catch (err) {
          info.accountsTable = { exists: false, error: err };
        }

        // Check if payments table exists and has required columns
        try {
          const { data: payments, error: paymentsError } = await supabase
            .from('payments')
            .select('*')
            .limit(1);

          info.paymentsTable = {
            exists: !paymentsError,
            error: paymentsError?.message,
            sampleData: payments?.[0] || null
          };
        } catch (err) {
          info.paymentsTable = { exists: false, error: err };
        }

        // Check storage bucket
        try {
          const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
          const paymentBucket = buckets?.find(b => b.id === 'payment-attachments');

          info.storageBucket = {
            exists: !!paymentBucket,
            error: bucketsError?.message,
            buckets: buckets?.map(b => b.id) || []
          };
        } catch (err) {
          info.storageBucket = { exists: false, error: err };
        }

        // Test a simple payment creation (without actually saving)
        try {
          const testPayment = {
            business_id: businessId,
            purchase_order_id: 'test-po-id',
            account_id: 'test-account-id',
            amount: 1000,
            payment_date: new Date().toISOString().split('T')[0],
            description: 'Test payment'
          };

          const { data, error } = await supabase
            .from('payments')
            .insert([testPayment])
            .select();

          info.testInsert = {
            success: !error,
            error: error?.message,
            data
          };

          // If test insert succeeded, delete it
          if (!error && data?.[0]?.id) {
            await supabase.from('payments').delete().eq('id', data[0].id);
          }
        } catch (err) {
          info.testInsert = { success: false, error: err };
        }

        setDebugInfo(info);
      } catch (err) {
        setDebugInfo({ error: err });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      checkDatabaseSetup();
    }
  }, [user, businessId]);

  if (loading) {
    return <div className="p-4">Loading debug information...</div>;
  }

  return (
    <div className="p-4 max-w-4xl">
      <h2 className="text-2xl font-bold mb-4">Payment Debug Information</h2>

      <div className="space-y-4">
        <div className="bg-gray-100 p-4 rounded">
          <h3 className="font-bold">User & Business Info</h3>
          <pre className="text-sm mt-2">{JSON.stringify({
            user: debugInfo.user,
            businessId: debugInfo.businessId,
            timestamp: debugInfo.timestamp
          }, null, 2)}</pre>
        </div>

        <div className="bg-blue-50 p-4 rounded">
          <h3 className="font-bold">Accounts Table</h3>
          <pre className="text-sm mt-2">{JSON.stringify(debugInfo.accountsTable, null, 2)}</pre>
        </div>

        <div className="bg-green-50 p-4 rounded">
          <h3 className="font-bold">Payments Table</h3>
          <pre className="text-sm mt-2">{JSON.stringify(debugInfo.paymentsTable, null, 2)}</pre>
        </div>

        <div className="bg-yellow-50 p-4 rounded">
          <h3 className="font-bold">Storage Bucket</h3>
          <pre className="text-sm mt-2">{JSON.stringify(debugInfo.storageBucket, null, 2)}</pre>
        </div>

        <div className="bg-red-50 p-4 rounded">
          <h3 className="font-bold">Test Payment Insert</h3>
          <pre className="text-sm mt-2">{JSON.stringify(debugInfo.testInsert, null, 2)}</pre>
        </div>

        {debugInfo.error && (
          <div className="bg-red-100 p-4 rounded">
            <h3 className="font-bold text-red-800">Error</h3>
            <pre className="text-sm mt-2 text-red-700">{JSON.stringify(debugInfo.error, null, 2)}</pre>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded">
        <h3 className="font-bold mb-2">Setup Instructions</h3>
        <ol className="list-decimal list-inside text-sm space-y-1">
          <li>Run the SQL in <code>PAYMENT_ACCOUNTS_SCHEMA.sql</code> in your Supabase SQL editor</li>
          <li>Make sure you have a business created and are logged in</li>
          <li>Check that all tables exist and have the required columns</li>
          <li>Verify that RLS policies are set up correctly</li>
          <li>Ensure the payment-attachments storage bucket exists</li>
        </ol>
      </div>
    </div>
  );
};

export default PaymentDebug;